import { Alert, Button, Card, Collapse, Space, Tag, Typography, theme } from 'antd';
import {
  BookOutlined,
  ExportOutlined,
  FileTextOutlined,
  LockOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { WorkflowStrip } from '@/components/enterprise/WorkflowStrip';
import { useAuthStore } from '@/store/authStore';
import type { HowToGuidePayload, HowToStep } from '@/types/help-guide';

const { Text, Paragraph, Title } = Typography;

const NOTE_PERMISSION = 'ai.memory.candidate.create';

function isOpenableRoute(route: string | undefined): route is string {
  return Boolean(route && route.startsWith('/') && !route.includes('/:'));
}

export function companyNoteLink(payload: Pick<HowToGuidePayload, 'guideId' | 'module' | 'title'>): string {
  const q = new URLSearchParams({
    newCandidate: '1',
    memoryType: 'process',
    domain: payload.module,
    subjectType: 'how_to_guide',
    subjectId: payload.guideId,
    subjectLabel: payload.title,
    title: payload.title,
    sourceType: 'HowToGuide',
  });
  return `/ai/enterprise-memory?${q.toString()}`;
}

export function useCanAddCompanyNote(): boolean {
  const user = useAuthStore((s) => s.user);
  return Boolean(user?.is_super_admin) || (user?.permissions ?? []).includes(NOTE_PERMISSION);
}

export interface HowToGuideCardProps {
  payload: HowToGuidePayload;
  compact?: boolean;
  /** Related-guide chip handler; chat sends a question, Help Center opens the guide. */
  onRelated?: (id: string, title: string) => void;
  /** Called before following an in-app link (e.g. close the assistant drawer). */
  onNavigate?: () => void;
  /** Help Center detail: no title row / Help Center link (page renders its own), troubleshooting expanded for print. */
  page?: boolean;
}

function StepRow({ step, onNavigate }: { step: HowToStep; onNavigate?: () => void }) {
  const { token } = theme.useToken();
  const openable = isOpenableRoute(step.route) && !step.routeBlocked;
  return (
    <li style={{ marginBottom: 10 }}>
      <Space size={6} wrap align="center">
        <Text strong>{step.title}</Text>
        {openable ? (
          <Link to={step.route!} onClick={onNavigate}>
            <Button size="small" type="link" icon={<ExportOutlined />} style={{ padding: 0, height: 'auto' }}>
              {step.routeLabel ? `Open ${step.routeLabel}` : 'Open screen'}
            </Button>
          </Link>
        ) : null}
        {step.routeBlocked ? (
          <Tag icon={<LockOutlined />} color="default">
            Screen not available to you
          </Tag>
        ) : null}
        {step.missingPermission ? (
          <Tag icon={<LockOutlined />} color="warning" title={step.missingPermission}>
            Needs permission: {step.missingPermission}
          </Tag>
        ) : null}
      </Space>
      <Paragraph style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>{step.detail}</Paragraph>
      {step.fields?.length ? (
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          Fields: {step.fields.join(', ')}
        </Text>
      ) : null}
      {step.tip ? (
        <Text style={{ fontSize: 12, display: 'block', color: token.colorInfoText }}>Tip: {step.tip}</Text>
      ) : null}
    </li>
  );
}

/** Step-by-step how-to guide tailored to the user (chat answer and Help Center detail). */
export function HowToGuideCard({ payload, compact, onRelated, onNavigate, page }: HowToGuideCardProps) {
  const { token } = theme.useToken();
  const canAddNote = useCanAddCompanyNote();
  const stages = payload.workflowStages.map((title) => ({ title }));

  return (
    <Card
      size="small"
      className="ai-agent-tool-result-card how-to-guide-card"
      styles={{ body: { padding: compact ? 8 : 12 } }}
      style={{ borderColor: token.colorBorderSecondary }}
    >
      {!page ? (
        <Space direction="vertical" size={2} style={{ width: '100%', marginBottom: 8 }}>
          <Space size={6} wrap>
            <BookOutlined style={{ color: token.colorPrimary }} />
            <Title level={5} style={{ margin: 0 }}>
              {payload.title}
            </Title>
            <Tag>{payload.moduleLabel}</Tag>
            {payload.source === 'general' ? <Tag color="orange">General guidance</Tag> : null}
          </Space>
          {payload.summary ? <Text type="secondary">{payload.summary}</Text> : null}
        </Space>
      ) : null}

      {payload.packUnavailable ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="This feature is not enabled for your company"
          description="Ask your administrator to install the related solution pack."
        />
      ) : null}

      {payload.prerequisites.length ? (
        <div style={{ marginBottom: 8 }}>
          <Text strong>Before you start</Text>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {payload.prerequisites.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {payload.steps.length ? (
        <div style={{ marginBottom: 8 }}>
          <Text strong>Steps</Text>
          <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {payload.steps.map((s) => (
              <StepRow key={`${s.index}-${s.title}`} step={s} onNavigate={onNavigate} />
            ))}
          </ol>
        </div>
      ) : null}

      {stages.length > 1 ? (
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Status flow
          </Text>
          <WorkflowStrip stages={stages} compact />
        </div>
      ) : null}

      {payload.exampleNumber ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Your next {payload.documentLabel || 'document'} number will look like <Text code>{payload.exampleNumber}</Text>
        </Text>
      ) : null}

      {payload.companyNotes.length ? (
        <Alert
          type="info"
          showIcon
          icon={<FileTextOutlined />}
          style={{ marginBottom: 8 }}
          message="Company procedure"
          description={
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {payload.companyNotes.map((n) => (
                <div key={n.memoryId}>
                  <Text strong>{n.title}</Text>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{n.detail || n.summary}</Paragraph>
                  {n.author ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Published by {n.author}
                    </Text>
                  ) : null}
                </div>
              ))}
            </Space>
          }
        />
      ) : null}

      {payload.troubleshooting.length ? (
        <Collapse
          size="small"
          style={{ marginBottom: 8 }}
          defaultActiveKey={page ? ['troubleshooting'] : undefined}
          items={[
            {
              key: 'troubleshooting',
              label: (
                <Space size={6}>
                  <WarningOutlined />
                  Troubleshooting ({payload.troubleshooting.length})
                </Space>
              ),
              children: (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {payload.troubleshooting.map((t) => (
                    <div key={t.symptom}>
                      <Text strong>{t.symptom}</Text>
                      <Paragraph style={{ margin: 0 }}>
                        <Text type="secondary">Cause:</Text> {t.cause}
                      </Paragraph>
                      <Paragraph style={{ margin: 0 }}>
                        <Text type="secondary">Fix:</Text> {t.fix}
                      </Paragraph>
                    </div>
                  ))}
                </Space>
              ),
            },
          ]}
        />
      ) : null}

      {payload.reports.length ? (
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ display: 'block' }}>
            Related reports
          </Text>
          <Space wrap size={[4, 4]}>
            {payload.reports
              .filter((r) => isOpenableRoute(r.path))
              .map((r) => (
                <Link key={r.path} to={r.path} onClick={onNavigate}>
                  <Button size="small" type="link" style={{ padding: 0 }}>
                    {r.label}
                  </Button>
                </Link>
              ))}
          </Space>
        </div>
      ) : null}

      {payload.related.length && onRelated ? (
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Related guides
          </Text>
          <Space wrap size={[6, 6]}>
            {payload.related.map((r) => (
              <Button key={r.id} size="small" onClick={() => onRelated(r.id, r.title)}>
                {r.title}
              </Button>
            ))}
          </Space>
        </div>
      ) : null}

      <Space wrap size={[8, 4]} className="no-print">
        {payload.source === 'guide' && !page ? (
          <Link to={`/help/${encodeURIComponent(payload.guideId)}`} onClick={onNavigate}>
            <Button size="small" type="link" icon={<BookOutlined />} style={{ padding: 0 }}>
              Open in Help Center
            </Button>
          </Link>
        ) : null}
        {canAddNote && payload.source === 'guide' ? (
          <Link to={companyNoteLink(payload)} onClick={onNavigate}>
            <Button size="small" type="link" icon={<PlusOutlined />} style={{ padding: 0 }}>
              Add company note
            </Button>
          </Link>
        ) : null}
      </Space>
    </Card>
  );
}
